import LegalLayout from './LegalLayout'
import { EyeOff, Trash2 } from 'lucide-react'


export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Política de Privacidad"
      subtitle="Cómo recopilamos, utilizamos, protegemos y eliminamos tus datos personales y los de tus clientes"
      lastUpdated="15 de octubre de 2026"
    >
      <div className="space-y-8 text-slate-700 leading-relaxed text-sm sm:text-base">
        {/* Intro */}
        <section className="space-y-3">
          <p>
            En <strong>IridiaChat</strong> (en adelante, la "Plataforma" o "nosotros"), accesible a través del dominio oficial <strong>https://iridiachat.com</strong>, respetamos su privacidad y nos comprometemos a garantizar la máxima seguridad, confidencialidad y transparencia en el tratamiento de los datos personales de nuestros clientes y de los usuarios finales que interactúan a través de los canales de mensajería integrados.
          </p>
          <p>
            Esta Política de Privacidad describe nuestras prácticas de tratamiento de datos personales conforme al marco normativo vigente en la República Bolivariana de Venezuela, estándares internacionales de privacidad y las directrices técnicas y de seguridad de <strong>Meta Platforms, Inc.</strong> para aplicaciones y Tech Providers de WhatsApp Business Platform.
          </p>
        </section>

        {/* 1. Responsable del Tratamiento y Datos de la Empresa */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">1</span>
            Responsable del Tratamiento y Datos Identificativos de la Empresa
          </h2>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 space-y-3 text-sm text-slate-700">
            <p className="font-medium text-slate-900">
              La plataforma tecnológica, software y marca comercial <strong>IridiaChat</strong> son de propiedad exclusiva y operados legal y comercialmente por:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-800">
              <li><strong>Razón Social:</strong> INVERSIONES MERCADO NARANJA, C.A.</li>
              <li><strong>Registro de Información Fiscal (RIF):</strong> J-502314539</li>
              <li><strong>Registro Mercantil:</strong> Inscrita ante el Registro Mercantil Primero del Distrito Capital en fecha 30 de mayo de 2022, bajo el N° 7, Tomo 304-A (Número de Expediente: 220-70295).</li>
              <li><strong>Domicilio Fiscal:</strong> Calle Los Frailes con Calle América, Casa S/N, Catia, Parroquia Sucre, Municipio Libertador, Caracas, Distrito Capital, Zona Postal 1030, Venezuela.</li>
              <li><strong>Sitio Web Oficial:</strong> <a href="https://iridiachat.com" className="text-emerald-700 underline font-semibold">https://iridiachat.com</a></li>
              <li><strong>Correo Electrónico de Privacidad y DPO:</strong> <a href="mailto:privacidad@iridiachat.com" className="text-emerald-700 font-semibold underline">privacidad@iridiachat.com</a></li>
            </ul>
          </div>
        </section>

        {/* 2. Datos que Recolectamos */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">2</span>
            Categorías de Datos Personales Recolectados
          </h2>
          <p>
            Para prestar el servicio SaaS de orquestación y atención comercial, recopilamos y procesamos las siguientes categorías de datos:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Datos de la Cuenta de Cliente / Empresa:</strong> Nombre comercial, nombre de contacto, correo electrónico de acceso, contraseña cifrada mediante algoritmos de alta seguridad (Argon2), rol del usuario dentro de la organización e identificador de tenant (inquilino).
            </li>
            <li>
              <strong>Datos de Integración de WhatsApp Business (Meta):</strong> Identificador de la cuenta de WhatsApp Business (WABA ID), Identificador del número de teléfono (Phone Number ID), nombre visible del negocio verificado por Meta y tokens de acceso otorgados mediante el Registro Incrustado (<em>Embedded Signup</em>).
            </li>
            <li>
              <strong>Datos de Conversaciones y Mensajería:</strong> Mensajes de texto, metadatos de entrega (recibido, leído, enviado), notas de voz o archivos multimedia remitidos voluntariamente por clientes finales para cotizaciones o consultas, número de teléfono del remitente e historial conversacional necesario para la continuidad de la atención.
            </li>
            <li>
              <strong>Datos de Comercio y Catálogo:</strong> Listados de productos, inventario, precios, borradores de pedidos generados en las conversaciones y solicitudes de citas o reservas.
            </li>
          </ul>
        </section>

        {/* 3. Finalidad del Tratamiento */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">3</span>
            Finalidad y Base Legal del Tratamiento
          </h2>
          <p>Tratamos la información personal exclusivamente con las siguientes finalidades legítimas:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Ejecución del servicio contratado:</strong> Enrutamiento de mensajes entre la API de Meta y el panel de atención en equipo.</li>
            <li><strong>Automatización asistida por Inteligencia Artificial:</strong> Generación de respuestas conversacionales mediante modelos de lenguaje alimentados únicamente con el catálogo y políticas del propio cliente.</li>
            <li><strong>Notificaciones operativas:</strong> Avisos de pedidos confirmados, alertas de escalado a tu equipo de atención y actualizaciones críticas del sistema.</li>
            <li><strong>Seguridad y prevención del fraude:</strong> Verificación de firmas criptográficas HMAC en los webhooks de Meta y prevención de accesos no autorizados.</li>
          </ul>
        </section>

        {/* 4. No Comercialización y Compromiso Anti-Venta de Datos */}
        <section className="space-y-3 bg-slate-50 border border-slate-200 rounded-xl p-5">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <EyeOff className="w-5 h-5 text-emerald-700" />
            4. Compromiso de No Venta ni Comercialización de Datos
          </h2>
          <p className="text-slate-800 font-semibold">
            IridiaChat bajo ninguna circunstancia vende, cede, alquila ni comercializa datos personales, números de teléfono o contenidos de conversaciones con terceras partes para fines publicitarios, de marketing de terceros o monetización de datos.
          </p>
          <p className="text-xs sm:text-sm text-slate-600">
            Los datos de cada negocio y de sus clientes pertenecen de manera exclusiva al titular del tenant correspondiente.
          </p>
        </section>

        {/* 5. Aislamiento Técnico y Seguridad */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">5</span>
            Seguridad Técnica y Aislamiento Multi-Inquilino (Multi-Tenant)
          </h2>
          <p>Implementamos estrictos controles de seguridad para salvaguardar la información:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Aislamiento por Tenant (tenantId):</strong> Todas las consultas en base de datos, búsquedas de catálogo y sesiones de memoria están aisladas por el identificador del inquilino. Es técnicamente imposible que un negocio acceda a conversaciones o datos de otro.</li>
            <li><strong>Cifrado en Tránsito y en Reposo:</strong> Toda comunicación viaja sobre protocolos seguros HTTPS con TLS 1.3. Los tokens de acceso y credenciales de la API de Meta se almacenan cifrados en reposo mediante algoritmos AES-256-GCM.</li>
            <li><strong>Idempotencia y Firmas Criptográficas:</strong> Cada mensaje recibido a través de los webhooks de Meta es validado mediante firma HMAC-SHA256 con el secreto de la aplicación (App Secret).</li>
          </ul>
        </section>

        {/* 6. Terceros y Transferencias Internacionales */}
        <section className="space-y-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">6</span>
            Proveedores de Infraestructura y Transferencias Necesarias
          </h2>
          <p>
            Para proveer las funcionalidades del sistema, interactuamos con los siguientes proveedores esenciales bajo acuerdos de confidencialidad y procesamiento de datos:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>Meta Platforms, Inc. (WhatsApp Cloud API):</strong> Para la recepción y despacho de mensajes en la red oficial de WhatsApp.</li>
            <li><strong>Servidores de alojamiento y base de datos:</strong> Infraestructura privada protegida con cortafuegos y respaldos cifrados.</li>
            <li><strong>Proveedores de Modelos de Inteligencia Artificial:</strong> Procesamiento de texto para la formulación de respuestas conversacionales dentro del marco de la interacción comercial.</li>
          </ul>
        </section>

        {/* 7. Derechos del Usuario y Eliminación de Datos */}
        <section className="space-y-3 bg-emerald-50/60 border border-emerald-200/80 rounded-xl p-5">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-emerald-700" />
            7. Tus Derechos y Eliminación de Datos (Data Deletion)
          </h2>
          <p className="text-slate-800">
            Conforme a la legislación vigente y a los requisitos de privacidad de Meta, todo usuario tiene derecho a solicitar el <strong>Acceso, Rectificación, Cancelación, Oposición y Supresión total</strong> de sus datos personales.
          </p>
          <div className="space-y-2 mt-2">
            <p>Disponemos de canales directos y automatizados para ejercer este derecho:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>
                <strong>Herramienta automatizada de eliminación:</strong> Puede ingresar a <a href="/eliminacion-datos" className="text-emerald-700 font-semibold underline">iridia-chat.juntaclick.com/eliminacion-datos</a> para solicitar la supresión de sus datos de forma instantánea y recibir un código oficial de confirmación.
              </li>
              <li>
                <strong>Eliminación directa de cuenta:</strong> Los usuarios registrados pueden eliminar su cuenta y perfil desde el panel administrativo en <em>Mi Perfil &gt; Zona de Peligro</em>.
              </li>
              <li>
                <strong>Desvinculación en Facebook:</strong> Los usuarios que conectaron WhatsApp pueden revocar permisos en cualquier momento desde la configuración de su cuenta de Facebook (<em>Configuración &gt; Apps y sitios web &gt; IridiaChat &gt; Eliminar</em>).
              </li>
            </ul>
          </div>
        </section>

        {/* 8. Contacto */}
        <section className="space-y-3 pt-4 border-t border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Contacto de Privacidad</h2>
          <p>
            Si tiene alguna duda sobre nuestra Política de Privacidad o el tratamiento de sus datos, contáctenos en:
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 inline-block">
            <p className="font-medium text-slate-900">Delegado de Protección de Datos (DPO)</p>
            <p className="text-slate-600 text-sm">Correo: privacidad@juntaclick.com</p>
            <p className="text-slate-600 text-sm">Soporte general: soporte@juntaclick.com</p>
          </div>
        </section>
      </div>
    </LegalLayout>
  )
}
